import { FeatureToggleService, hd } from '@nx-fe/services';
import { IDeviceContext, IDeviceContextHandler, ResponseData, IDeviceInput, IDeviceScreenTransformService, InstallParameters, IBaseDeviceContext, TransferOperation } from '../mcrc-api';
import { Session } from '../session';
import { MultiScreenContext } from './multiScreen';
import { FileSystemContext } from './fileSystem';
import { SecurityContext } from './securityContext';
import { IInitRequestPacket, IServerStatus, ISystemInfo, SignatureByte } from 'app/modules/remote-control-ng/definitions';
import { BehaviorSubject, Observable } from "rxjs";
import { RcConfigurationService } from '../services/rcConfigurationService';
import { DeviceContext, IRcDeviceContext, IRemoteControlStreamingContext } from './deviceContext';
import { DeviceContextHandler } from './deviceContextHandler';
import { DeviceCanvas } from './deviceCanvas';
import { DeviceInputService } from '../services/deviceInputService';
import { DeviceScreenTransformService } from '../services/deviceScreenTransformService';
import { Injector } from '@angular/core';

export class RcContext {

    // replace mcrc.appDeviceView.options.deviceContext
    bitDepth: number;
    imageQuality: number

    deviceId: string;
    sotiAssistDeviceId: string;
    userName: string;
    rcToken: string = null;
    deviceName: string;
    deviceKind: string;
    sessionId: string;
    host: string;
    versionHi: number;
    versionLo: number;
    disableUserDisplay: boolean;
    disableUserInteraction: boolean;
    forceOverride: boolean = false;
    initialized: boolean = false;
    rcResponseData: ResponseData = null;
    ticketId: string = null;
    featureToggleService: FeatureToggleService = <any>{ isEnabled() { return false; }, isSettingsLoaded: false } as FeatureToggleService;
    serverStatus: IServerStatus;
    draftDeviceCaptureActivity: hd.IDeviceCaptureActivity;
    recordedVideoName: string;
    videoActivityId: number;
    skinName: string;
    noActivityTimeout: number;
    // compressionLevel: number;
    // connectionTimeout: number;
    // sensitivity: number;

    private _deviceInput: IDeviceInput = null;
    public get deviceInput(): IDeviceInput {
        return this._deviceInput;
    }

    public get sharedResponseData(): ResponseData {
        return this.deviceContext.videoStreaming ? this.deviceContext.remoteControlSharedData.data : this.rcResponseData;
    }

    // RcContext could become a destruction managed Singleton, and FileSystemContext
    // would become a simple member and not be a singleton
    fileSystem: FileSystemContext = new FileSystemContext();
    multiScreen: MultiScreenContext = new MultiScreenContext();
    security: SecurityContext = new SecurityContext();

    // System info
    private _systemInfo: ISystemInfo;
    private _systemInfoChange: BehaviorSubject<ISystemInfo> = new BehaviorSubject<ISystemInfo>(null);
    public systemInfoChange$: Observable<ISystemInfo> = this._systemInfoChange.asObservable();

    public get systemInfo(): ISystemInfo {
        return this._systemInfo;
    }
    public set systemInfo(systemInfo: ISystemInfo) {
        this._systemInfo = systemInfo;
        this._systemInfoChange.next(this._systemInfo);
        this.session.appUI?.getSystemInfoUI()?.update(systemInfo);
    }

    private _deviceCanvas: DeviceCanvas = null;
    public get deviceCanvas(): DeviceCanvas {
        return this._deviceCanvas;
    }

    private _deviceScreenTransformService: IDeviceScreenTransformService = null;
    public get deviceScreenTransformService(): IDeviceScreenTransformService {
        return this._deviceScreenTransformService;
    }

    // TODO 2025.1: deviceContext and getDeviceContextHandler().currentContext()
    // return different structures. The code should be changed to get the same device
    // related information, the skin and other relevant data. All of it should be
    // maintained in the RcContext's property deviceContext
    private _deviceContext: IBaseDeviceContext = null;
    public get deviceContext(): IBaseDeviceContext {
        return this._deviceContext;
    }

    private _deviceContextHandler: IDeviceContextHandler = null;
    public get deviceContextHandler(): IDeviceContextHandler {
        return this._deviceContextHandler;
    }

    private _session: Session;
    public get session(): Session {
        if (!this._session) {
            this._session = new Session();
        }
        return this._session;
    }

    private _isStreaming: boolean = false;
    public get isStreaming(): boolean { return this._isStreaming; }

    private rcConfiguration: RcConfigurationService;

    private static _instance: RcContext;
    private static _injector: Injector;

    private constructor() {
        // private constructor, no direct instantiation
    }

    public static get instance() {
        return this._instance || (this._instance = new this());
    }

    public static setInjector(injector: Injector): void {
        this._injector = injector;
    }

    public getService<T>(service: new (...args: any[]) => T): T {
        return RcContext._injector.get(service);
    }

    /**
     * appDeviceView code was merged into the RC context
     * */
    private init = (transferOperation?: TransferOperation) => {
        this._isStreaming = false;
        this.multiScreen = new MultiScreenContext();
        this._deviceCanvas = new DeviceCanvas();
        const deviceContext = new DeviceContext(this._deviceCanvas);
        this._deviceContext = <IBaseDeviceContext>(deviceContext);
        this._deviceContextHandler = new DeviceContextHandler();

        this._deviceScreenTransformService = new DeviceScreenTransformService(deviceContext, this._deviceContextHandler);

        this._deviceCanvas.init();
        this._deviceInput = this.getService(DeviceInputService);
        this._deviceInput.deviceContext = this._deviceContext;

        let initPacket: IInitRequestPacket;
        // Only when transferring an already started RC session we don't want to send a PC_INIT
        if (transferOperation !== TransferOperation.transfer) {
            // prepare the session info
            initPacket = {
                deviceId: this.deviceId,
                sotiAssistDeviceId: this.sotiAssistDeviceId,
                userName: this.userName,
                token: this.rcToken,
                sessionId: this.sessionId,
                keys: this.session.constructInitKeys(false, 100, Number(this.ticketId), this.deviceName),
                versionHigh: this.versionHi,
                versionLow: this.versionLo,
                host: this.host,
                disableUserDisplay: this.disableUserDisplay,
                disableUserInteraction: this.disableUserInteraction,
                forceOverride: this.forceOverride
            };
            this.forceOverride = false;
        }

        this.session.init(this.deviceCanvas, this.deviceInput, this.deviceScreenTransformService, this.deviceContext as IRcDeviceContext, this.deviceContextHandler, initPacket);
        this.initialized = true;
    }

    public initVideoStreamingContext(context: IRemoteControlStreamingContext) {
        // for video streaming, we only use DeviceInput and a part of deviceContext so that some js code can be compatibly re-used
        this._isStreaming = true;
        this.multiScreen = new MultiScreenContext();
        this._deviceContext = <any>context as IRcDeviceContext;
        this._deviceInput = this.getService(DeviceInputService);
        this._deviceInput.deviceContext = context;

        this.session.appUI.init(undefined);

        this.deviceInput.registerMouseInputListeners(document.getElementById('rcInputCanvas'));

        this._deviceContextHandler = null;
        this._deviceScreenTransformService = null;
        this.initialized = true;
    }

    public clear = () => {
        this.clearStandardRc();

        if (this.deviceCanvas) {
            this.deviceCanvas.clear();
        }

        if (this.deviceScreenTransformService) {
            this.deviceScreenTransformService.shutdown();
            this._deviceScreenTransformService = undefined;
        }

        if (this.deviceInput) {
            const canvas = this.deviceContext.videoStreaming ? document.getElementById('rcInputCanvas') : (<IDeviceContext>this.deviceContext).canvas?.element;
            this.deviceInput.unregisterMouseInputListeners(canvas);
            this.deviceInput.unregisterKeyInputListeners();

            /* clear the skin */
            if (!this.deviceContext.videoStreaming) {
                (<IDeviceContext>this.deviceContext).clearSkin();
            }
            this._deviceInput = null;
        }

        this.session.unsubscribeFromSocketMessages();
        this.rcResponseData = null;
        this.rcToken = null;
        this.ticketId = null;
        this.forceOverride = false;
        this.featureToggleService = { isEnabled() { return false; }, isSettingsLoaded: false } as any;

        this.initialized = false;
    }

    public configure(): void {
        // the only configuration done at session startup
        // id the RC logging level
        if (!this.rcConfiguration) {
            this.rcConfiguration = this.getService(RcConfigurationService);
        }
        this.rcConfiguration.configure();
    }

    public clearStandardRc() {
        this.versionHi = undefined;
        this.versionLo = undefined;
        if (this._deviceContextHandler) {
            this._deviceContextHandler.updateWithSessionEnd();
        }
        this.initialized = false;
    }

    public install(parameters: InstallParameters, startPoppedOut: boolean) {
        this.clearStandardRc();
        this.configure();
        this.session.createConnection(parameters);
        if (!startPoppedOut) {
            this.init(undefined);
        }
        this.initialized = true;
    };

    public transfer(oldRc: RcContext, featureToggleService: FeatureToggleService, transferOperation: TransferOperation) {
        const wasAlreadyInitialized = this.initialized;
        if (!wasAlreadyInitialized) {
            this.setResponseData(oldRc.rcResponseData);
            this.ticketId = oldRc.ticketId;
            this.featureToggleService = featureToggleService;
            this.configure();
            this.initialized = true;
        }

        this.session.getConnection().transfer(oldRc.session.getSocket());
        // integrated from the JS appDeviceView transfer
        if (!wasAlreadyInitialized) {
            this.init(transferOperation);
        }
        if (transferOperation === TransferOperation.transfer) {
            // for streaming deviceInput is initialized by initVideoStreamingContext()
            // Still, the session state needs to be transferred
            if (!this.isStreaming) {
                (this.deviceContext as IRcDeviceContext).transfer(<any>oldRc.deviceContext);
                this.deviceContextHandler.transfer(<any>oldRc.deviceContextHandler.currentContext() );
            }
            this.multiScreen.copy(oldRc.multiScreen);
            this.session.transfer(oldRc.session);

            if (!this.deviceContext.videoStreaming) {
                if (this.deviceScreenTransformService) {
                    this.deviceScreenTransformService.updateAfterTransfer();
                    this.deviceScreenTransformService.transferPanZoom(oldRc);
                }
            }

        }

        this.initialized = true;
    };

    public setResponseData(rcResponseData: ResponseData) {
        this.rcResponseData = rcResponseData;

        this.rcToken = rcResponseData.RemoteControlSecurityToken;
        this.versionHi = rcResponseData.AgentVersionHigh;
        this.versionLo = rcResponseData.AgentVersionLow;
        // this.compressionLevel = rcResponseData.CompressionLevel;
        // this.connectionTimeout = rcResponseData.ConnectionTimeout;
        this.deviceId = rcResponseData.DeviceId;
        this.deviceName = rcResponseData.DeviceName;
        this.deviceKind = rcResponseData.DeviceKind;
        this.disableUserDisplay = rcResponseData.DisableUserDisplay;
        this.disableUserInteraction = rcResponseData.DisableUserIntraction; // The incorrect spelling of this property is coming from the MC API
        this.host = rcResponseData.Host;
        this.noActivityTimeout = rcResponseData.NoActivityTimeout;
        // this.sensitivity = rcResponseData.Sensitivity;
        this.sessionId = rcResponseData.SessionId;
        this.skinName = rcResponseData.SkinName;
        this.userName = rcResponseData.UserName;
        this.sotiAssistDeviceId = rcResponseData.SotiAssistDeviceId;
    };
}
